import React from "react";
import { motion } from "framer-motion";
import { Star, Phone } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_derma-studio-2/artifacts/u4xe0yh1_Beauty%20Aesthetic%20013.jpeg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative min-h-screen pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden grain"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-center">
        {/* Left text column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="md:col-span-7 lg:col-span-6 relative z-10"
        >
          <p className="eyebrow mb-6">Daisy Hill · Queensland</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl leading-[1.02] tracking-tight text-[#2D2926] mb-6">
            Integrated
            <br />
            <em className="italic text-[#C89F95] font-light">health &amp; skin</em>,
            <br />
            quietly luminous.
          </h1>
          <p className="eyebrow text-[#C89F95] mb-8" data-testid="hero-tagline">
            Skin · Weight · Women's Health · Hair &amp; Scalp
          </p>
          <p className="text-base md:text-lg text-[#5C5753] leading-relaxed max-w-xl mb-10 font-light">
            A boutique nurse-led studio for integrated health and skin — combining advanced
            aesthetics, medical weight management, women's wellness and hair &amp; scalp
            therapy under one calm, considered roof.
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="#book"
              data-testid="hero-reserve-btn"
              className="bg-[#C89F95] hover:bg-[#B3867B] text-white px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors"
            >
              Book Now
            </a>
            <a
              href="tel:0459830505"
              data-testid="hero-call-btn"
              className="border border-[#2D2926]/20 hover:border-[#C89F95] text-[#2D2926] px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors flex items-center gap-2"
            >
              <Phone size={14} strokeWidth={1.5} /> VIP Enquiries
            </a>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1 text-[#D4A373]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C5753]">
              5.0 · Reviewed by 200+ clients
            </p>
          </div>
        </motion.div>

        {/* Right image column */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="md:col-span-5 lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-sm">
            <img
              src={HERO_IMG}
              alt="Glowing skin with warm sunlight"
              data-testid="hero-image"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2D2926]/30 via-transparent to-transparent" />
          </div>

          {/* floating accolade card */}
          <motion.div
            initial={{ opacity: 0, x: 30, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
            className="hidden md:block absolute -bottom-8 -left-8 bg-[#FDFBF7] border border-[#E8DFD8] p-6 max-w-[260px] shadow-sm"
            data-testid="hero-accolade"
          >
            <p className="eyebrow text-[#C89F95] mb-3">Integrated Care</p>
            <p className="font-serif text-lg leading-snug text-[#2D2926]">
              Skin. Weight Management. Women's health. Hair. One nurse-led roof.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* decorative side text */}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 rotate-90 origin-right">
        <p className="eyebrow text-[#C89F95]">Est · Daisy Hill · 2018</p>
      </div>
    </section>
  );
}
